import React, { useState, useEffect, useMemo } from 'react';
import {
  Users, Target, Play, Pause, Edit, Trash2, Plus, Gift, Award, Percent, X, AlertTriangle
} from 'lucide-react';

/** Types **/
interface Campaign {
  id: string;
  name: string;
  type: 'discount' | 'giveaway' | 'loyalty';
  startDate: string;
  endDate: string;
  participants: number;
  budget: number;
  spent: number;
  status: 'draft' | 'active' | 'paused' | 'completed';
  conversion: number;
  reach: number;
  engagement: number;
}

/* Confirmation Dialog Component */
interface ConfirmDialogProps {
  isOpen: boolean;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  onConfirm: () => void;
  onCancel: () => void;
  type?: 'danger' | 'warning' | 'info';
}

const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  isOpen,
  title,
  message,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  onConfirm,
  onCancel,
  type = 'info'
}) => {
  if (!isOpen) return null;

  const getTypeStyles = () => {
    switch (type) {
      case 'danger':
        return {
          icon: <Trash2 className="w-6 h-6 text-red-600" />,
          confirmButton: 'bg-red-600 hover:bg-red-700 text-white'
        };
      case 'warning':
        return {
          icon: <AlertTriangle className="w-6 h-6 text-yellow-600" />,
          confirmButton: 'bg-yellow-600 hover:bg-yellow-700 text-white'
        };
      default:
        return {
          icon: <AlertTriangle className="w-6 h-6 text-blue-600" />,
          confirmButton: 'bg-blue-600 hover:bg-blue-700 text-white'
        };
    }
  };

  const styles = getTypeStyles();

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md">
        <div className="flex items-center gap-3 mb-4">
          {styles.icon}
          <h3 className="text-lg font-medium text-gray-900">{title}</h3>
        </div>

        <p className="text-gray-600 mb-6">{message}</p>

        <div className="flex justify-end gap-3">
          <button
            onClick={onCancel}
            className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            {cancelText}
          </button>
          <button
            onClick={onConfirm}
            className={`px-4 py-2 rounded-lg ${styles.confirmButton}`}
          >
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
};

const CampaignsPage: React.FC = () => {
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState<Campaign | null>(null);

  // Confirmation dialog state (explicit handlers instead of storing functions in object)
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmTitle, setConfirmTitle] = useState('');
  const [confirmMessage, setConfirmMessage] = useState('');
  const [confirmType, setConfirmType] = useState<'danger' | 'warning' | 'info'>('info');
  const [confirmAction, setConfirmAction] = useState<(() => void) | null>(null);

  const [campaignForm, setCampaignForm] = useState({
    name: '',
    description: '',
    rewardType: 'discount',
    startDate: '',
    endDate: '',
    budget: '',
    targetAudience: '',
    prizeValue: '',
    maxParticipants: ''
  });

  useEffect(() => {
    loadCampaigns();
  }, []);

  const loadCampaigns = async () => {
    try {
      setLoading(true);
      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 800));

      setCampaigns([
        {
          id: '1',
          name: 'Summer Savings',
          type: 'discount',
          startDate: '2024-07-01',
          endDate: '2024-08-31',
          participants: 1250,
          budget: 15000,
          spent: 8750,
          status: 'active',
          conversion: 22.5,
          reach: 5500,
          engagement: 12.8
        },
        {
          id: '2',
          name: 'Back to School Giveaway',
          type: 'giveaway',
          startDate: '2024-08-15',
          endDate: '2024-09-15',
          participants: 890,
          budget: 8000,
          spent: 4200,
          status: 'active',
          conversion: 15.2,
          reach: 3200,
          engagement: 18.7
        },
        {
          id: '3',
          name: 'Loyalty Rewards Program',
          type: 'loyalty',
          startDate: '2024-06-01',
          endDate: '2024-12-31',
          participants: 2847,
          budget: 25000,
          spent: 12800,
          status: 'active',
          conversion: 28.9,
          reach: 8900,
          engagement: 24.5
        },
        {
          id: '4',
          name: 'Black Friday Sale',
          type: 'discount',
          startDate: '2024-11-24',
          endDate: '2024-11-30',
          participants: 0,
          budget: 20000,
          spent: 0,
          status: 'draft',
          conversion: 0,
          reach: 0,
          engagement: 0
        },
        {
          id: '5',
          name: 'Holiday Giveaway',
          type: 'giveaway',
          startDate: '2024-12-01',
          endDate: '2024-12-25',
          participants: 456,
          budget: 12000,
          spent: 3200,
          status: 'paused',
          conversion: 8.7,
          reach: 2100,
          engagement: 15.3
        }
      ]);
    } catch (err) {
      console.error('Failed to load campaigns:', err);
      setCampaigns([]);
    } finally {
      setLoading(false);
    }
  };

  // ------------- Validation helpers -------------
  const isFormValid = useMemo(() => {
    const nameOk = campaignForm.name.trim().length > 0;
    const start = campaignForm.startDate ? new Date(campaignForm.startDate) : null;
    const end = campaignForm.endDate ? new Date(campaignForm.endDate) : null;
    const datesOk = start && end && end >= start;
    const budgetNum = Number(campaignForm.budget);
    const budgetOk = !isNaN(budgetNum) && isFinite(budgetNum) && budgetNum >= 0;
    return nameOk && datesOk && budgetOk;
  }, [campaignForm]);

  // ------------- CRUD-like handlers -------------
  const handleCreateCampaign = async (e?: React.FormEvent | React.MouseEvent) => {
    e?.preventDefault?.();

    // Client-side validation
    if (!isFormValid) {
      setConfirmType('warning');
      setConfirmTitle('Invalid Input');
      setConfirmMessage('Please provide a campaign name, valid start & end dates (end >= start) and a non-negative budget.');
      setConfirmAction(() => () => setConfirmOpen(false));
      setConfirmOpen(true);
      return;
    }

    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 500));

      const id = (typeof (window as any).crypto?.randomUUID === 'function')
        ? (window as any).crypto.randomUUID()
        : Math.random().toString(36).substring(2, 10);

      const newCampaign: Campaign = {
        id,
        name: campaignForm.name.trim(),
        type: campaignForm.rewardType as 'discount' | 'giveaway' | 'loyalty',
        startDate: campaignForm.startDate,
        endDate: campaignForm.endDate,
        participants: 0,
        budget: Number(campaignForm.budget) || 0,
        spent: 0,
        status: 'draft',
        conversion: 0,
        reach: 0,
        engagement: 0
      };

      setCampaigns(prev => [...prev, newCampaign]);
      setShowCreateModal(false);
      setCampaignForm({
        name: '',
        description: '',
        rewardType: 'discount',
        startDate: '',
        endDate: '',
        budget: '',
        targetAudience: '',
        prizeValue: '',
        maxParticipants: ''
      });

      setConfirmType('info');
      setConfirmTitle('Success');
      setConfirmMessage('Campaign created successfully!');
      setConfirmAction(() => () => setConfirmOpen(false));
      setConfirmOpen(true);
    } catch (err: any) {
      setConfirmType('danger');
      setConfirmTitle('Error');
      setConfirmMessage(`Failed to create campaign: ${err?.message || 'Unknown error'}`);
      setConfirmAction(() => () => setConfirmOpen(false));
      setConfirmOpen(true);
    }
  };

  const handleCampaignAction = async (id: string, action: 'start' | 'pause') => {
    try {
      await new Promise(resolve => setTimeout(resolve, 300));
      setCampaigns(prev => prev.map(c =>
        c.id === id ? { ...c, status: action === 'start' ? 'active' : 'paused' } : c
      ));
      setConfirmType('info');
      setConfirmTitle('Success');
      setConfirmMessage(`Campaign ${action}ed successfully!`);
      setConfirmAction(() => () => setConfirmOpen(false));
      setConfirmOpen(true);
    } catch (err: any) {
      setConfirmType('danger');
      setConfirmTitle('Error');
      setConfirmMessage(`Failed to ${action} campaign: ${err?.message || 'Unknown error'}`);
      setConfirmAction(() => () => setConfirmOpen(false));
      setConfirmOpen(true);
    }
  };

  const handleDeleteCampaign = (campaign: Campaign) => {
    setConfirmType('danger');
    setConfirmTitle('Delete Campaign');
    setConfirmMessage(`Are you sure you want to delete "${campaign.name}"? This action cannot be undone.`);
    setConfirmAction(() => () => {
      setCampaigns(prev => prev.filter(c => c.id !== campaign.id));
      setConfirmOpen(false);
    });
    setConfirmOpen(true);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800 border border-green-200';
      case 'paused': return 'bg-yellow-100 text-yellow-800 border border-yellow-200';
      case 'completed': return 'bg-blue-100 text-blue-800 border border-blue-200';
      case 'draft': return 'bg-gray-100 text-gray-800 border border-gray-200';
      default: return 'bg-gray-100 text-gray-800 border border-gray-200';
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'discount': return <Percent className="w-4 h-4" />;
      case 'giveaway': return <Gift className="w-4 h-4" />;
      case 'loyalty': return <Award className="w-4 h-4" />;
      default: return <Target className="w-4 h-4" />;
    }
  };

  // Loading UI
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading campaigns...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Marketing Campaigns</h1>
              <p className="text-gray-600 mt-2">Manage and track your marketing campaigns</p>
            </div>
            <button
              onClick={() => setShowCreateModal(true)}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center gap-2 transition-colors"
            >
              <Plus className="w-4 h-4" />
              Create Campaign
            </button>
          </div>
        </div>

        {/* Analytics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          <div className="bg-white p-6 rounded-lg shadow-sm border">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Campaigns</p>
                <p className="text-2xl font-bold text-gray-900">{campaigns.length}</p>
              </div>
              <Target className="w-8 h-8 text-blue-600" />
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-sm border">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Active Campaigns</p>
                <p className="text-2xl font-bold text-gray-900">
                  {campaigns.filter(c => c.status === 'active').length}
                </p>
              </div>
              <Play className="w-8 h-8 text-green-600" />
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-sm border">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Participants</p>
                <p className="text-2xl font-bold text-gray-900">
                  {campaigns.reduce((sum, c) => sum + c.participants, 0).toLocaleString()}
                </p>
              </div>
              <Users className="w-8 h-8 text-purple-600" />
            </div>
          </div>
        </div>

        {/* Campaigns List */}
        <div className="bg-white rounded-lg shadow-sm border">
          <div className="p-6 border-b">
            <h2 className="text-xl font-semibold text-gray-900">All Campaigns</h2>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Campaign</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Duration</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Participants</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Budget</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Conversion</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {campaigns.map((campaign) => (
                  <tr key={campaign.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">{campaign.name}</div>
                        <div className="text-sm text-gray-500">ID: {campaign.id}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        {getTypeIcon(campaign.type)}
                        <span className="text-sm capitalize">{campaign.type}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      <div>{campaign.startDate}</div>
                      <div className="text-gray-500">to {campaign.endDate}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {campaign.participants.toLocaleString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      <div>${campaign.spent.toLocaleString()} / ${campaign.budget.toLocaleString()}</div>
                      <div className="w-full bg-gray-200 rounded-full h-2 mt-1">
                        <div
                          className="bg-blue-600 h-2 rounded-full transition-all"
                          style={{ width: `${Math.min((campaign.spent / (campaign.budget || 1)) * 100, 100) || 0}%` }}
                        ></div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {campaign.conversion}%
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(campaign.status)}`}>
                        {campaign.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center gap-2">
                        {campaign.status === 'draft' || campaign.status === 'paused' ? (
                          <button
                            onClick={() => handleCampaignAction(campaign.id, 'start')}
                            className="text-green-600 hover:text-green-900 transition-colors"
                            title="Start Campaign"
                          >
                            <Play className="w-4 h-4" />
                          </button>
                        ) : campaign.status === 'active' ? (
                          <button
                            onClick={() => handleCampaignAction(campaign.id, 'pause')}
                            className="text-yellow-600 hover:text-yellow-900 transition-colors"
                            title="Pause Campaign"
                          >
                            <Pause className="w-4 h-4" />
                          </button>
                        ) : null}

                        <button
                          onClick={() => setSelectedCampaign(campaign)}
                          className="text-blue-600 hover:text-blue-900 transition-colors"
                          title="Edit Campaign"
                        >
                          <Edit className="w-4 h-4" />
                        </button>

                        <button
                          onClick={() => handleDeleteCampaign(campaign)}
                          className="text-red-600 hover:text-red-900 transition-colors"
                          title="Delete Campaign"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Create Campaign Modal */}
        {showCreateModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium">Create New Campaign</h3>
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Campaign Name</label>
                  <input
                    type="text"
                    value={campaignForm.name}
                    onChange={(e) => setCampaignForm(prev => ({ ...prev, name: e.target.value }))}
                    className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter campaign name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Campaign Type</label>
                  <select
                    value={campaignForm.rewardType}
                    onChange={(e) => setCampaignForm(prev => ({ ...prev, rewardType: e.target.value }))}
                    className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="discount">Discount Campaign</option>
                    <option value="giveaway">Giveaway Campaign</option>
                    <option value="loyalty">Loyalty Program</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <textarea
                    value={campaignForm.description}
                    onChange={(e) => setCampaignForm(prev => ({ ...prev, description: e.target.value }))}
                    className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    rows={3}
                    placeholder="Describe your campaign"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                    <input
                      type="date"
                      value={campaignForm.startDate}
                      onChange={(e) => setCampaignForm(prev => ({ ...prev, startDate: e.target.value }))}
                      className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
                    <input
                      type="date"
                      value={campaignForm.endDate}
                      onChange={(e) => setCampaignForm(prev => ({ ...prev, endDate: e.target.value }))}
                      className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Budget ($)</label>
                  <input
                    type="number"
                    value={campaignForm.budget}
                    onChange={(e) => setCampaignForm(prev => ({ ...prev, budget: e.target.value }))}
                    className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    min="0"
                    step="0.01"
                    placeholder="0.00"
                  />
                </div>

                <div className="flex justify-end gap-2 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowCreateModal(false)}
                    className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={(e) => handleCreateCampaign(e)}
                    className={`px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors ${!isFormValid ? 'opacity-60 cursor-not-allowed' : ''}`}
                    disabled={!isFormValid}
                  >
                    Create Campaign
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Confirmation Dialog */}
        <ConfirmDialog
          isOpen={confirmOpen}
          title={confirmTitle}
          message={confirmMessage}
          onConfirm={() => confirmAction?.()}
          onCancel={() => setConfirmOpen(false)}
          type={confirmType}
          confirmText="OK"
          cancelText="Cancel"
        />
      </div>
    </div>
  );
};

export default CampaignsPage;